import { isOpenQuoteAndOrderCalendarModal } from "@/atoms/is-open-quote-and-order-calendar-modal";
import { orderPageState } from "@/atoms/order/order-page-state";
import { pageNumberState } from "@/atoms/order/page-number-state";
import { quoteAndOrderDurationState } from "@/atoms/quote-and-order-duration-state";
import { DEBOUNCE_TIME } from "@/common/constant/debounce-time";
import { GLOBAL_ERROR } from "@/common/constant/global-error";
import { PaginationSize } from "@/common/enum/pagination-size";
import { OrderSortType } from "@/common/enum/partner-manage-order-sort";
import { OrderBar } from "@/components/bar/OrderBar";
import { AdminOrderHeader } from "@/components/header/AdminOrderHeader";
import { PartnerInfoModal } from "@/components/modal/PartnerInfoModal";
import { PartnerManageOrderPageModalPanel } from "@/components/panel/PartnerManageOrderPageModalPanel";
import { AdminOrderTable } from "@/components/table/admin-order-table/AdminOrderTable";
import { PageWrapper } from "@/components/wrapper/PageWrapper";
import { orderService } from "@/services/order-service2";
import { DateRange } from "@/types/date-range";
import { BasePageDto } from "@/types/dto/base-page-dto";
import { OrderDetailDto } from "@/types/dto/order-detail-dto";
import { OrderPagePayload } from "@/types/payload/order-page-payload";
import { debounce } from "lodash";
import { useCallback, useEffect, useState } from "react";
import { useRecoilState } from "recoil";

export const AdminOrderPage = () => {
  const [keyword, setKeyword] = useState<string>("");
  const [selectedDuration, setSelectedDuration] = useRecoilState<DateRange>(
    quoteAndOrderDurationState
  );
  const [modal, setModal] = useRecoilState(isOpenQuoteAndOrderCalendarModal);
  const [sortType, setSortType] = useState<OrderSortType>(OrderSortType.ALL);
  const [page, setPage] =
    useRecoilState<BasePageDto<OrderDetailDto>>(orderPageState);

  const [isPartnerInfoModalOpen, setIsPartnerInfoModalOpen] = useState<
    number | undefined
  >();
  const openPartnerInfoModal = (userId: number) => {
    setIsPartnerInfoModalOpen(userId);
  };

  const closePartnerInfoModal = () => {
    setIsPartnerInfoModalOpen(undefined);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setKeyword(event.target.value);
  };

  const handleSortChange = (sortType: OrderSortType) => {
    setSortType(sortType);
  };

  const getPage = async (input: OrderPagePayload) => {
    try {
      const response = await orderService.getPage(input);
      if (!response.ok) {
        return alert(GLOBAL_ERROR);
      }
      setPage(response.data);
    } catch (error) {
      console.log(error);
    }
  };

  const [pageNumber, setPageNumber] = useRecoilState(pageNumberState);

  const getPageDebounce = useCallback(
    debounce(async (input) => {
      await getPage(input);
    }, DEBOUNCE_TIME),
    []
  );

  useEffect(() => {
    const payload = {
      page: pageNumber,
      size: PaginationSize.SIZE_12,
      fromDate: modal.startDate || selectedDuration.startDate,
      toDate: modal.endDate || selectedDuration.endDate,
      orderType: sortType === OrderSortType.ALL ? undefined : sortType,
      keyword: keyword,
    };
    getPageDebounce(payload);
  }, [pageNumber]);

  useEffect(() => {
    const payload = {
      page: 1,
      size: PaginationSize.SIZE_12,
      fromDate: modal.startDate || selectedDuration.startDate,
      toDate: modal.endDate || selectedDuration.endDate,
      orderType: sortType === OrderSortType.ALL ? undefined : sortType,
      keyword: keyword,
    };
    getPageDebounce(payload);
    setPageNumber(1);
  }, [
    sortType,
    modal.startDate,
    modal.endDate,
    selectedDuration.startDate,
    selectedDuration.endDate,
    keyword,
  ]);

  return (
    <PageWrapper>
      <div className="flex flex-col w-[1648px]">
        <AdminOrderHeader />
        <OrderBar
          placeholder="발주번호 or 상호명 or 파트너명 검색"
          totalElements={page.totalElements}
          keyword={keyword}
          orderSortType={sortType}
          handleSearchChange={handleSearchChange}
          handleSortChange={handleSortChange}
        />
        <AdminOrderTable
          openPartnerInfoModal={openPartnerInfoModal}
          currentFilters={{
            fromDate: modal.startDate || selectedDuration.startDate,
            toDate: modal.endDate || selectedDuration.endDate,
            orderType: sortType === OrderSortType.ALL ? undefined : sortType,
            keyword: keyword,
          }}
        />
      </div>
      {isPartnerInfoModalOpen && (
        <PartnerInfoModal
          closeModal={closePartnerInfoModal}
          userId={isPartnerInfoModalOpen}
        />
      )}
      <PartnerManageOrderPageModalPanel />
    </PageWrapper>
  );
};
